import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Upload, Save, ImageIcon, Users as UsersIcon, LayoutGrid, CheckCircle2, XCircle, Flame, Plus, Trash2, Pencil, Film, ScrollText, Scale } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import GenerationLogAdmin from "@/components/GenerationLogAdmin";
import DemoAccountsAdmin from "@/components/DemoAccountsAdmin";
import LegalContactAdmin from "@/components/LegalContactAdmin";

function ImageSlot({ url, onChange, testid }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const b64 = await new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(f); });
      const { data } = await api.post("/uploads/image", { image_base64: b64, origin_url: window.location.origin });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex gap-3">
      <div className="relative w-20 h-20 shrink-0 rounded-lg overflow-hidden border border-zinc-800 bg-ink-950">
        {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-600"><ImageIcon size={18} /></div>}
      </div>
      <div className="flex-1 flex flex-col gap-2">
        <input value={url || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" data-testid={`${testid}-url`}
          className="w-full bg-ink-950 border border-zinc-800 rounded px-3 py-2 text-xs text-zinc-300 focus:border-gold focus:outline-none" />
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`${testid}-upload`}
          className="flex items-center gap-2 self-start text-xs text-gold border border-gold/40 rounded px-3 py-1.5 hover:bg-[#1a140d] transition-colors">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><div className="label-mono mb-1.5">{label}</div>{children}</div>
);
const inputCls = "w-full bg-ink-950 border border-zinc-800 rounded px-3 py-2 text-sm focus:border-gold focus:outline-none";

function VideoSlot({ url, onChange, testid }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Please use a short loop under 25MB"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const b64 = await new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(f); });
      const { data } = await api.post("/uploads/video", { video_base64: b64, origin_url: window.location.origin });
      onChange(data.url);
      toast.success("Video uploaded");
    } catch { toast.error("Video upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex gap-3">
      <div className="relative w-20 h-20 shrink-0 rounded-lg overflow-hidden border border-zinc-800 bg-ink-950">
        {url ? <video src={url} muted loop autoPlay playsInline className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-600"><Film size={18} /></div>}
      </div>
      <div className="flex-1 flex flex-col gap-2">
        <input value={url || ""} onChange={(e) => onChange(e.target.value)} placeholder="Loop video URL" data-testid={`${testid}-url`}
          className="w-full bg-ink-950 border border-zinc-800 rounded px-3 py-2 text-xs text-zinc-300 focus:border-gold focus:outline-none" />
        <input ref={fileRef} type="file" accept="video/*" onChange={upload} className="hidden" />
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`${testid}-upload`}
          className="flex items-center gap-2 self-start text-xs text-gold border border-gold/40 rounded px-3 py-1.5 hover:bg-[#1a140d] transition-colors">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload loop
        </button>
      </div>
    </div>
  );
}

const EMPTY = {
  title: "", description: "", category: "Trending", output_type: "image", input_type: "image",
  ai_model: "gemini-3.1-flash-image-preview", hidden_prompt: "", allow_user_prompt: false,
  user_prompt_label: "Add an optional detail (optional)", thumbnail: "", preview_video: "", loop_on_home: false,
  example_outputs: [], credit_cost: 5, featured: false, trending: false, order: 0, active: true,
  video_duration: 5, video_resolution: "720p", video_aspect: "adaptive",
};

function TemplateEditor({ tpl, cfg, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...tpl });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const models = f.output_type === "video" ? (cfg.video_models || []) : (cfg.image_models || []);

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = { ...f, credit_cost: Number(f.credit_cost) || 0, order: Number(f.order) || 0,
      video_duration: Number(f.video_duration) || 5,
      example_outputs: Array.isArray(f.example_outputs) ? f.example_outputs : String(f.example_outputs || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (f.id) await api.put(`/admin/templates/${f.id}`, payload);
      else await api.post("/admin/templates", payload);
      toast.success("Template saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8" data-testid="template-editor">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-ink-900 border border-zinc-800 rounded-2xl p-6 space-y-4 mx-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-black uppercase tracking-tight text-xl">{f.id ? "Edit" : "New"} Template</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><XCircle size={22} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Title"><input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="tpl-title" className={inputCls} /></Field>
          <Field label="Category">
            <select value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="tpl-category" className={inputCls}>
              {(cfg.template_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Description (visible to users)"><textarea value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="tpl-desc" rows={2} className={inputCls} /></Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Output">
            <select value={f.output_type} onChange={(e) => { const ot = e.target.value; set("output_type", ot); set("ai_model", (ot === "video" ? cfg.video_models : cfg.image_models)?.[0] || ""); }} data-testid="tpl-output" className={inputCls}>
              <option value="image">Image</option><option value="video">Video</option>
            </select>
          </Field>
          <Field label="Input">
            <select value={f.input_type} onChange={(e) => set("input_type", e.target.value)} data-testid="tpl-input" className={inputCls}>
              <option value="image">Single image</option><option value="images">Multiple images</option><option value="images_text">Multiple images + text</option><option value="text">Text only</option>
            </select>
          </Field>
          <Field label="AI Model">
            <select value={f.ai_model} onChange={(e) => set("ai_model", e.target.value)} data-testid="tpl-model" className={inputCls}>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
        </div>

        <Field label="🔒 Hidden system prompt (never shown to users)">
          <textarea value={f.hidden_prompt} onChange={(e) => set("hidden_prompt", e.target.value)} data-testid="tpl-hidden" rows={4} className={`${inputCls} font-mono text-xs`} placeholder="Transform the uploaded subject into…" />
        </Field>

        {f.output_type === "video" && (
          <div className="grid grid-cols-3 gap-4">
            <Field label="Duration (s)"><input type="number" value={f.video_duration} onChange={(e) => set("video_duration", e.target.value)} className={inputCls} /></Field>
            <Field label="Resolution">
              <select value={f.video_resolution} onChange={(e) => set("video_resolution", e.target.value)} className={inputCls}><option>480p</option><option>720p</option><option>1080p</option></select>
            </Field>
            <Field label="Aspect"><input value={f.video_aspect} onChange={(e) => set("video_aspect", e.target.value)} className={inputCls} /></Field>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Thumbnail"><ImageSlot url={f.thumbnail} onChange={(v) => set("thumbnail", v)} testid="tpl-thumb" /></Field>
          <Field label="Preview loop video (upload from phone)">
            <VideoSlot url={f.preview_video} onChange={(v) => set("preview_video", v)} testid="tpl-preview" />
            <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2"><input type="checkbox" checked={f.loop_on_home} onChange={(e) => set("loop_on_home", e.target.checked)} data-testid="tpl-loop-home" /> Autoplay loop on homepage</label>
          </Field>
        </div>

        <Field label="Example output URLs (comma separated)">
          <input value={Array.isArray(f.example_outputs) ? f.example_outputs.join(", ") : f.example_outputs} onChange={(e) => set("example_outputs", e.target.value)} data-testid="tpl-examples" className={inputCls} />
        </Field>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
          <Field label="Credit cost"><input type="number" value={f.credit_cost} onChange={(e) => set("credit_cost", e.target.value)} data-testid="tpl-cost" className={inputCls} /></Field>
          <Field label="Display order"><input type="number" value={f.order} onChange={(e) => set("order", e.target.value)} data-testid="tpl-order" className={inputCls} /></Field>
          <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="tpl-featured" /> Featured</label>
          <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={f.trending} onChange={(e) => set("trending", e.target.checked)} data-testid="tpl-trending" /> Trending</label>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="tpl-active" /> Active (visible)</label>
          <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={f.allow_user_prompt} onChange={(e) => set("allow_user_prompt", e.target.checked)} data-testid="tpl-allowprompt" /> Allow optional user prompt</label>
        </div>

        <button onClick={save} disabled={saving} data-testid="tpl-save" className="w-full flex items-center justify-center gap-2 bg-gold text-ink-950 font-heading font-bold py-3 rounded-md hover:bg-goldlight transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Template
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("templates");
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState(null);
  const [stats, setStats] = useState({ total: 0, subscribed: 0 });
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template object or {} for new
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!loading && (!user || user.role !== "admin")) navigate("/home"); }, [user, loading, navigate]);

  const loadTemplates = () => api.get("/admin/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  useEffect(() => {
    api.get("/site-config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => { setUsers(data.users); setStats({ total: data.total, subscribed: data.subscribed }); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    loadTemplates();
  }, []);

  const saveImages = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/site-config", cfg); setCfg(data); toast.success("Saved ✨"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const setFeatured = (i, k, v) => setCfg((c) => ({ ...c, featured: c.featured.map((f, j) => j === i ? { ...f, [k]: v } : f) }));
  const setTrending = (i, k, v) => setCfg((c) => ({ ...c, trending: c.trending.map((t, j) => j === i ? { ...t, [k]: v } : t) }));

  const setSlide = (i, k, v) => setCfg((c) => ({ ...c, home_slides: (c.home_slides || []).map((s, j) => j === i ? { ...s, [k]: v } : s) }));
  const addSlide = () => setCfg((c) => ({ ...c, home_slides: [...(c.home_slides || []), { img: "", title: "", subtitle: "", cta_label: "", link: "" }] }));
  const delSlide = (i) => setCfg((c) => ({ ...c, home_slides: (c.home_slides || []).filter((_, j) => j !== i) }));
  const moveSlide = (i, dir) => setCfg((c) => {
    const arr = [...(c.home_slides || [])]; const j = i + dir;
    if (j < 0 || j >= arr.length) return c;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...c, home_slides: arr };
  });
  const setBanner = (i, k, v) => setCfg((c) => ({ ...c, home_banners: (c.home_banners || []).map((b, j) => j === i ? { ...b, [k]: v } : b) }));
  const addBanner = () => setCfg((c) => ({ ...c, home_banners: [...(c.home_banners || []), { img: "", caption: "", link: "" }] }));
  const delBanner = (i) => setCfg((c) => ({ ...c, home_banners: (c.home_banners || []).filter((_, j) => j !== i) }));

  const delTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/admin/templates/${id}`); toast.success("Deleted"); loadTemplates(); }
    catch { toast.error("Delete failed"); }
  };
  const toggleBypass = async (u) => {
    try { const { data } = await api.put(`/admin/users/${u.id}/bypass`, { bypass_credits: !u.bypass_credits });
      setUsers((us) => us.map((x) => x.id === u.id ? { ...x, bypass_credits: data.bypass_credits } : x)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveCatalog = async () => {
    try { const { data } = await api.put("/admin/site-config", { template_categories: cfg.template_categories, image_models: cfg.image_models, video_models: cfg.video_models }); setCfg((c) => ({ ...c, ...data })); toast.success("Catalog saved"); }
    catch { toast.error("Save failed"); }
  };
  const csv = (arr) => (Array.isArray(arr) ? arr.join(", ") : arr || "");
  const setCsv = (k, v) => setCfg((c) => ({ ...c, [k]: v.split(",").map((s) => s.trim()).filter(Boolean) }));

  if (!cfg) return <div className="min-h-screen bg-ink-950 flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)} data-testid={`admin-tab-${id}`} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-heading transition-colors ${tab === id ? "bg-[#F0E9DD] text-ink-950" : "bg-ink-900 border border-white/10 text-zinc-400"}`}><Icon size={16} /> {label}</button>
  );

  return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD]">
      <header className="bg-ink-950 border-b border-white/5 sticky top-0 z-30 backdrop-blur-xl bg-ink-950/85">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/home")} data-testid="admin-back" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-gold transition-colors"><ArrowLeft size={18} /> Back</button>
          <h1 className="font-heading font-black uppercase tracking-tight text-lg sm:text-xl">Admin Studio</h1>
          {tab === "images"
            ? <button onClick={saveImages} disabled={saving} data-testid="admin-save" className="flex items-center gap-2 bg-gold text-ink-950 font-heading font-bold text-sm px-4 py-2 rounded-md hover:bg-goldlight transition-colors disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
            : <span className="w-16" />}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 mb-8 flex-wrap">
          <TabBtn id="templates" icon={LayoutGrid} label="Templates" />
          <TabBtn id="images" icon={ImageIcon} label="Homepage Images" />
          <TabBtn id="users" icon={UsersIcon} label="Users" />
          <TabBtn id="log" icon={ScrollText} label="Generation Log" />
          <TabBtn id="demo" icon={UsersIcon} label="Demo Accounts" />
          <TabBtn id="legal" icon={Scale} label="Legal & Contact" />
        </div>

        {tab === "log" && <GenerationLogAdmin />}
        {tab === "demo" && <DemoAccountsAdmin />}
        {tab === "legal" && <LegalContactAdmin />}

        {tab === "templates" && (
          <div className="space-y-6" data-testid="admin-templates-panel">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-black uppercase tracking-tight text-2xl">Template Manager</h2>
              <button onClick={() => setEditing({})} data-testid="admin-new-template" className="flex items-center gap-2 bg-gold text-ink-950 font-heading font-bold text-sm px-4 py-2 rounded-md hover:bg-goldlight transition-colors"><Plus size={16} /> New Template</button>
            </div>

            <div className="border border-white/10 rounded-xl p-4 bg-ink-900/60 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Categories (comma)"><input value={csv(cfg.template_categories)} onChange={(e) => setCsv("template_categories", e.target.value)} data-testid="admin-catalog-categories" className={inputCls} onBlur={saveCatalog} /></Field>
              <Field label="Image models (comma)"><input value={csv(cfg.image_models)} onChange={(e) => setCsv("image_models", e.target.value)} data-testid="admin-catalog-image-models" className={inputCls} onBlur={saveCatalog} /></Field>
              <Field label="Video models (comma)"><input value={csv(cfg.video_models)} onChange={(e) => setCsv("video_models", e.target.value)} data-testid="admin-catalog-video-models" className={inputCls} onBlur={saveCatalog} /></Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.length === 0 && <p className="text-zinc-500 col-span-full">No templates yet. Click "New Template".</p>}
              {templates.map((t) => (
                <div key={t.id} className="border border-white/10 rounded-xl overflow-hidden bg-ink-900/60" data-testid="admin-template-row">
                  <div className="relative aspect-video bg-ink-950">
                    {t.thumbnail ? <img src={t.thumbnail} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-700"><ImageIcon /></div>}
                    <div className="absolute top-2 left-2 flex gap-1">
                      {t.trending && <span className="bg-gold text-ink-950 text-[9px] font-bold rounded-full px-2 py-0.5">HOT</span>}
                      {!t.active && <span className="bg-red-500/80 text-white text-[9px] font-bold rounded-full px-2 py-0.5">HIDDEN</span>}
                      <span className="bg-ink-950/80 text-[9px] font-bold rounded-full px-2 py-0.5">{t.output_type}</span>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="font-heading font-bold text-sm truncate">{t.title}</div>
                    <div className="text-[11px] text-zinc-500">{t.category} · {t.credit_cost} cr · {t.ai_model?.split("-")[0]}</div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setEditing(t)} data-testid="admin-edit-template" className="flex-1 flex items-center justify-center gap-1 text-xs border border-zinc-700 rounded py-1.5 hover:border-gold hover:text-gold transition-colors"><Pencil size={12} /> Edit</button>
                      <button onClick={() => delTemplate(t.id)} data-testid="admin-delete-template" className="flex items-center justify-center gap-1 text-xs border border-zinc-700 rounded py-1.5 px-3 hover:border-red-500 hover:text-red-400 transition-colors"><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "images" && (
          <div className="space-y-10" data-testid="admin-images-panel">
            <section data-testid="admin-slides-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="label-mono flex items-center gap-3"><span className="w-8 h-px bg-gold" /> Top Slideshow (Explore AI Templates)</h2>
                <button onClick={addSlide} data-testid="admin-add-slide" className="flex items-center gap-1.5 text-xs text-gold border border-gold/40 rounded px-3 py-1.5 hover:bg-[#1a140d] transition-colors"><Plus size={13} /> Add slide</button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {(cfg.home_slides || []).map((s, i) => (
                  <div key={i} className="border border-white/10 rounded-xl p-4 bg-ink-900/60 space-y-3" data-testid={`admin-slide-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="label-mono">Slide {i + 1}</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => moveSlide(i, -1)} className="text-zinc-400 hover:text-gold p-1" title="Move up">↑</button>
                        <button onClick={() => moveSlide(i, 1)} className="text-zinc-400 hover:text-gold p-1" title="Move down">↓</button>
                        <button onClick={() => delSlide(i)} data-testid={`admin-slide-${i}-del`} className="text-zinc-400 hover:text-red-400 p-1"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <ImageSlot url={s.img} onChange={(v) => setSlide(i, "img", v)} testid={`admin-slide-${i}-img`} />
                    <input value={s.title || ""} onChange={(e) => setSlide(i, "title", e.target.value)} placeholder="Headline" data-testid={`admin-slide-${i}-title`} className={inputCls} />
                    <input value={s.subtitle || ""} onChange={(e) => setSlide(i, "subtitle", e.target.value)} placeholder="Subtitle" data-testid={`admin-slide-${i}-subtitle`} className={inputCls} />
                    <div className="grid grid-cols-2 gap-3">
                      <input value={s.cta_label || ""} onChange={(e) => setSlide(i, "cta_label", e.target.value)} placeholder="Button label (optional)" data-testid={`admin-slide-${i}-cta`} className={inputCls} />
                      <input value={s.link || ""} onChange={(e) => setSlide(i, "link", e.target.value)} placeholder="Link e.g. /t/<id> (optional)" data-testid={`admin-slide-${i}-link`} className={inputCls} />
                    </div>
                  </div>
                ))}
                {(cfg.home_slides || []).length === 0 && <p className="text-zinc-500">No slides yet. Click "Add slide".</p>}
              </div>
            </section>
            <section>
              <h2 className="label-mono mb-4 flex items-center gap-3"><span className="w-8 h-px bg-gold" /> Hero Background</h2>
              <div className="border border-white/10 rounded-xl p-4 bg-ink-900/60 max-w-xl"><ImageSlot url={cfg.hero_image} onChange={(v) => setCfg((c) => ({ ...c, hero_image: v }))} testid="admin-hero" /></div>
            </section>
            <section data-testid="admin-banners-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="label-mono flex items-center gap-3"><span className="w-8 h-px bg-gold" /> Feature Banners (between template rows)</h2>
                <button onClick={addBanner} data-testid="admin-add-banner" className="flex items-center gap-1.5 text-xs text-gold border border-gold/40 rounded px-3 py-1.5 hover:bg-[#1a140d] transition-colors"><Plus size={13} /> Add banner</button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {(cfg.home_banners || []).map((b, i) => (
                  <div key={i} className="border border-white/10 rounded-xl p-4 bg-ink-900/60 space-y-3" data-testid={`admin-banner-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="label-mono">Banner {i + 1}</span>
                      <button onClick={() => delBanner(i)} data-testid={`admin-banner-${i}-del`} className="text-zinc-400 hover:text-red-400 p-1"><Trash2 size={14} /></button>
                    </div>
                    <ImageSlot url={b.img} onChange={(v) => setBanner(i, "img", v)} testid={`admin-banner-${i}-img`} />
                    <input value={b.caption || ""} onChange={(e) => setBanner(i, "caption", e.target.value)} placeholder="Caption (optional)" data-testid={`admin-banner-${i}-caption`} className={inputCls} />
                    <input value={b.link || ""} onChange={(e) => setBanner(i, "link", e.target.value)} placeholder="Link (optional)" data-testid={`admin-banner-${i}-link`} className={inputCls} />
                  </div>
                ))}
              </div>
            </section>
            <section>
              <h2 className="label-mono mb-4 flex items-center gap-3"><span className="w-8 h-px bg-gold" /> Featured Creations</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {(cfg.featured || []).map((f, i) => (
                  <div key={i} className="border border-white/10 rounded-xl p-4 bg-ink-900/60 space-y-3" data-testid={`admin-featured-${i}`}>
                    <ImageSlot url={f.img} onChange={(v) => setFeatured(i, "img", v)} testid={`admin-featured-${i}-img`} />
                    <input value={f.tag || ""} onChange={(e) => setFeatured(i, "tag", e.target.value)} placeholder="Title" data-testid={`admin-featured-${i}-tag`} className={inputCls} />
                    <input value={f.meta || ""} onChange={(e) => setFeatured(i, "meta", e.target.value)} placeholder="Meta" data-testid={`admin-featured-${i}-meta`} className={inputCls} />
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {tab === "users" && (
          <div data-testid="admin-users-panel">
            <div className="flex gap-4 mb-6">
              <div className="border border-white/10 rounded-xl px-5 py-4 bg-ink-900/60"><div className="label-mono">Total Users</div><div className="font-heading font-black text-3xl mt-1" data-testid="admin-total-users">{stats.total}</div></div>
              <div className="border border-gold/40 rounded-xl px-5 py-4 bg-[#140f0a]"><div className="label-mono">Subscribed</div><div className="font-heading font-black text-3xl mt-1 text-gold" data-testid="admin-subscribed-users">{stats.subscribed}</div></div>
            </div>
            <div className="border border-white/10 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-900 text-left label-mono"><tr>
                  <th className="px-4 py-3">Email</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Subscribed</th><th className="px-4 py-3">Credits</th><th className="px-4 py-3">Credit Bypass</th>
                </tr></thead>
                <tbody>
                  {!users ? <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" /></td></tr>
                    : users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5 hover:bg-ink-900/50" data-testid="admin-user-row">
                      <td className="px-4 py-3 font-mono" data-testid="admin-user-email">{u.email}</td>
                      <td className="px-4 py-3 capitalize text-zinc-300">{u.plan}{u.role === "admin" ? " (admin)" : ""}</td>
                      <td className="px-4 py-3">{u.subscribed ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 size={14} /> Yes</span> : <span className="inline-flex items-center gap-1 text-zinc-500 text-xs"><XCircle size={14} /> No</span>}</td>
                      <td className="px-4 py-3 font-mono text-gold">{u.role === "admin" && u.bypass_credits ? "∞" : u.credits}</td>
                      <td className="px-4 py-3">
                        {u.role === "admin"
                          ? <button onClick={() => toggleBypass(u)} data-testid="admin-bypass-toggle" className={`text-xs px-3 py-1 rounded-full border transition-colors ${u.bypass_credits ? "border-gold text-gold bg-[#1a140d]" : "border-zinc-700 text-zinc-500"}`}>{u.bypass_credits ? "ON" : "OFF"}</button>
                          : <span className="text-zinc-600 text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editing && <TemplateEditor tpl={editing.id ? editing : {}} cfg={cfg} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadTemplates(); }} />}
    </div>
  );
}
